/**
 * Toast Component & Toast Manager
 *
 * Lightweight notification system for user feedback.
 * Features:
 * - Multiple toast variants (success, error, warning, info)
 * - Auto-dismiss with configurable duration
 * - Manual dismiss option
 * - Stacked toasts in top-right corner
 * - Smooth animations (slide in/out)
 * - Toast manager for programmatic usage
 */

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

interface ToastItemProps extends Toast {
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({
  id,
  variant,
  title,
  message,
  dismissible = true,
  onDismiss,
}) => {
  const variants = {
    success: {
      bg: 'bg-success-50',
      border: 'border-success-500',
      text: 'text-success-800',
      icon: <CheckCircle2 className="w-5 h-5 text-success-600" />,
    },
    error: {
      bg: 'bg-error-50',
      border: 'border-error-500',
      text: 'text-error-800',
      icon: <XCircle className="w-5 h-5 text-error-600" />,
    },
    warning: {
      bg: 'bg-warning-50',
      border: 'border-warning-500',
      text: 'text-warning-800',
      icon: <AlertCircle className="w-5 h-5 text-warning-600" />,
    },
    info: {
      bg: 'bg-info-50',
      border: 'border-info-500',
      text: 'text-info-800',
      icon: <Info className="w-5 h-5 text-info-600" />,
    },
  };

  const style = variants[variant];

  return (
    <div
      className={`
        ${style.bg} ${style.border} border-l-4 rounded-lg shadow-lg p-4 min-w-[320px] max-w-md
        animate-slide-in-right
      `}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">{style.icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <p className={`font-semibold ${style.text} mb-1`}>{title}</p>
          )}
          <p className={`text-sm ${style.text}`}>{message}</p>
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <button
            onClick={() => onDismiss(id)}
            className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-neutral-600" />
          </button>
        )}
      </div>
    </div>
  );
};

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      duration: 5000,
      dismissible: true,
      ...toast,
    };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Limit number of toasts
      if (updated.length > maxToasts) {
        return updated.slice(updated.length - maxToasts);
      }
      return updated;
    });

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, [maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAll }}>
      {children}

      {/* Toast Container */}
      <div
        className={`fixed ${positionClasses[position]} z-50 flex flex-col gap-3 pointer-events-none`}
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem {...toast} onDismiss={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Convenience hooks for common toast types
export const useToastHelpers = () => {
  const { addToast } = useToast();

  return {
    success: (message: string, title?: string) =>
      addToast({ variant: 'success', message, title }),
    error: (message: string, title?: string) =>
      addToast({ variant: 'error', message, title }),
    warning: (message: string, title?: string) =>
      addToast({ variant: 'warning', message, title }),
    info: (message: string, title?: string) =>
      addToast({ variant: 'info', message, title }),
  };
};

export default ToastProvider;
